import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Pencil,
  Trash2,
  Target,
  TrendingUp,
  TrendingDown,
  Info,
  AlertTriangle,
  History,
} from "lucide-react";

// Helper to format percentage
const pct = (n) => `${Math.round((Number(n) || 0) * 100)}%`;

// Helper to format currency
const currency = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

// Scope type labels
const SCOPE_LABELS = {
  global: "Global",
  product: "Product",
  campaign: "Campaign",
  agent: "Agent",
  script: "Script",
};

// Scope type colors
const SCOPE_COLORS = {
  global: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  product: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  campaign: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  agent: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  script: "bg-purple-500/20 text-purple-300 border-purple-500/30",
};

/**
 * Edit/Create Benchmark Dialog
 */
function BenchmarkDialog({
  open,
  onOpenChange,
  benchmark,
  kpiMetadata,
  onSave,
  mode = "create",
}) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  // Form state
  const [kpiName, setKpiName] = useState(benchmark?.kpi_name || benchmark?.metric || "");
  const [targetValue, setTargetValue] = useState(benchmark?.target_value?.toString() || "");
  const [warningThreshold, setWarningThreshold] = useState(
    (benchmark?.variance_threshold_warning || benchmark?.variance_threshold || 0.1).toString()
  );
  const [criticalThreshold, setCriticalThreshold] = useState(
    (benchmark?.variance_threshold_critical || 0.2).toString()
  );
  const [scopeType, setScopeType] = useState(benchmark?.scope_type || "global");
  const [scopeId, setScopeId] = useState(benchmark?.scope_id || "");

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setKpiName(benchmark?.kpi_name || benchmark?.metric || "");
      setTargetValue(benchmark?.target_value?.toString() || "");
      setWarningThreshold(
        (benchmark?.variance_threshold_warning || benchmark?.variance_threshold || 0.1).toString()
      );
      setCriticalThreshold((benchmark?.variance_threshold_critical || 0.2).toString());
      setScopeType(benchmark?.scope_type || "global");
      setScopeId(benchmark?.scope_id || "");
    }
  }, [open, benchmark]);

  // Get selected KPI details
  const selectedKpi = useMemo(() => {
    return kpiMetadata?.supported_kpis?.find((k) => k.kpi_name === kpiName);
  }, [kpiMetadata, kpiName]);

  // Format input label based on KPI type
  const getTargetLabel = () => {
    if (!selectedKpi) return "Target Value";
    if (kpiName.includes("rate") || kpiName.includes("conversion")) {
      return "Target Value (decimal, e.g., 0.35 for 35%)";
    }
    if (kpiName.includes("revenue") || kpiName.includes("cpa") || kpiName.includes("profit")) {
      return "Target Value ($)";
    }
    if (kpiName.includes("hours")) {
      return "Target Value (hours)";
    }
    return "Target Value";
  };

  const handleSubmit = async () => {
    if (!kpiName) {
      toast({ title: "KPI is required", variant: "destructive" });
      return;
    }
    if (!targetValue || isNaN(parseFloat(targetValue))) {
      toast({ title: "Valid target value is required", variant: "destructive" });
      return;
    }
    if (scopeType !== "global" && !scopeId.trim()) {
      toast({ title: "Scope ID is required for non-global scope", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        kpi_name: kpiName,
        metric: kpiName,
        target_value: parseFloat(targetValue),
        variance_threshold: parseFloat(warningThreshold) || 0.1,
        variance_threshold_warning: parseFloat(warningThreshold) || 0.1,
        variance_threshold_critical: parseFloat(criticalThreshold) || 0.2,
        scope_type: scopeType,
        scope_id: scopeType === "global" ? null : scopeId.trim(),
      };

      if (mode === "edit" && benchmark?.id) {
        await api.put(`/analytics/benchmarks/${benchmark.id}`, payload);
        toast({ title: "Benchmark updated" });
      } else {
        await api.post("/analytics/benchmarks", payload);
        toast({ title: "Benchmark created" });
      }

      onSave?.();
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Failed to save benchmark",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-lg"
        data-testid="benchmark-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="benchmark-dialog-title">
            {mode === "edit" ? "Edit Benchmark" : "Add Benchmark"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* KPI Selection */}
          <div className="space-y-2">
            <Label className="text-slate-200/80">KPI</Label>
            <Select value={kpiName} onValueChange={setKpiName} disabled={mode === "edit"}>
              <SelectTrigger
                className="bg-white/5 text-slate-100 border-white/10"
                data-testid="benchmark-kpi-select"
              >
                <SelectValue placeholder="Select KPI" />
              </SelectTrigger>
              <SelectContent>
                {kpiMetadata?.supported_kpis?.map((kpi) => (
                  <SelectItem key={kpi.kpi_name} value={kpi.kpi_name}>
                    {kpi.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Polarity indicator */}
            {selectedKpi && (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                {selectedKpi.polarity === "higher_is_better" ? (
                  <>
                    <TrendingUp className="h-3 w-3 text-emerald-400" />
                    <span>Higher is better</span>
                  </>
                ) : (
                  <>
                    <TrendingDown className="h-3 w-3 text-blue-400" />
                    <span>Lower is better</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Scope Type */}
          <div className="space-y-2">
            <Label className="text-slate-200/80">Scope</Label>
            <Select value={scopeType} onValueChange={setScopeType} disabled={mode === "edit"}>
              <SelectTrigger
                className="bg-white/5 text-slate-100 border-white/10"
                data-testid="benchmark-scope-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="global">Global (applies to all)</SelectItem>
                <SelectItem value="product">Product-specific</SelectItem>
                <SelectItem value="campaign">Campaign-specific</SelectItem>
                <SelectItem value="agent">Agent-specific</SelectItem>
                <SelectItem value="script">Script-specific</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Scope ID (conditional) */}
          {scopeType !== "global" && (
            <div className="space-y-2">
              <Label className="text-slate-200/80">
                {SCOPE_LABELS[scopeType]} ID
              </Label>
              <Input
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
                className="bg-white/5 ring-1 ring-white/10"
                placeholder={`Enter ${scopeType} ID`}
                disabled={mode === "edit"}
                data-testid="benchmark-scope-id-input"
              />
            </div>
          )}

          {/* Target Value */}
          <div className="space-y-2">
            <Label className="text-slate-200/80">{getTargetLabel()}</Label>
            <Input
              type="number"
              step="any"
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
              className="bg-white/5 ring-1 ring-white/10"
              data-testid="benchmark-target-input"
            />
          </div>

          {/* Thresholds */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-200/80 text-sm">
                Warning Threshold
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 ml-1 inline text-slate-400" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-slate-900 border-slate-700 text-xs">
                      Yellow status if variance exceeds this %
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
              <Input
                type="number"
                step="0.01"
                value={warningThreshold}
                onChange={(e) => setWarningThreshold(e.target.value)}
                className="bg-white/5 ring-1 ring-white/10"
                placeholder="0.10"
                data-testid="benchmark-warning-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200/80 text-sm">
                Critical Threshold
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 ml-1 inline text-slate-400" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-slate-900 border-slate-700 text-xs">
                      Red status if variance exceeds this %
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
              <Input
                type="number"
                step="0.01"
                value={criticalThreshold}
                onChange={(e) => setCriticalThreshold(e.target.value)}
                className="bg-white/5 ring-1 ring-white/10"
                placeholder="0.20"
                data-testid="benchmark-critical-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="benchmark-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            data-testid="benchmark-save-btn"
          >
            {saving ? "Saving…" : mode === "edit" ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Delete Confirmation Dialog
 */
function DeleteBenchmarkDialog({ open, onOpenChange, benchmark, onDelete }) {
  const { toast } = useToast();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!benchmark?.id) return;

    setDeleting(true);
    try {
      await api.delete(`/analytics/benchmarks/${benchmark.id}`);
      toast({ title: "Benchmark deleted" });
      onDelete?.();
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Failed to delete benchmark",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const kpiDisplay = benchmark?.kpi_name || benchmark?.metric || "Unknown";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-md"
        data-testid="delete-benchmark-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-300">
            <AlertTriangle className="h-5 w-5" />
            Delete Benchmark
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-slate-300">
            Are you sure you want to delete this benchmark?
          </p>
          <div className="rounded-lg bg-white/5 p-3 space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-400 text-sm">KPI:</span>
              <span className="text-slate-100">{kpiDisplay}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 text-sm">Scope:</span>
              <Badge className={`text-xs border ${SCOPE_COLORS[benchmark?.scope_type] || SCOPE_COLORS.global}`}>
                {SCOPE_LABELS[benchmark?.scope_type] || "Global"}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 text-sm">Target:</span>
              <span className="text-slate-100 font-mono">{benchmark?.target_value}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="border-slate-600 text-slate-200 hover:bg-slate-800"
            onClick={() => onOpenChange(false)}
            data-testid="delete-benchmark-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            className="bg-rose-500/90 text-white hover:bg-rose-500"
            onClick={handleDelete}
            disabled={deleting}
            data-testid="delete-benchmark-confirm-btn"
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Main Benchmark Manager Component
 * 
 * P0 TENANT ISOLATION: Accepts tenantId prop for SuperAdmin cross-tenant access.
 * Refetches data and resets state when tenantId changes.
 */
export default function BenchmarkManager({ tenantId }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [benchmarks, setBenchmarks] = useState([]);
  const [kpiMetadata, setKpiMetadata] = useState(null);

  // Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBenchmark, setSelectedBenchmark] = useState(null);
  const [dialogMode, setDialogMode] = useState("create");

  // Filter state
  const [scopeFilter, setScopeFilter] = useState("all");

  // Fetch benchmarks and KPI metadata
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // P0 TENANT ISOLATION: Pass tenant_id for SuperAdmin cross-tenant access
      const params = tenantId ? { tenant_id: tenantId } : {};
      const [benchmarksRes, metadataRes] = await Promise.all([
        api.get("/analytics/benchmarks", { params }),
        api.get("/analytics/benchmarks/kpi-metadata"),
      ]);
      setBenchmarks(benchmarksRes.data || []);
      setKpiMetadata(metadataRes.data);
    } catch (e) {
      toast({
        title: "Failed to load benchmarks",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId, toast]);

  // P0 TENANT ISOLATION: Refetch data and reset state when tenant changes
  useEffect(() => {
    // Reset dialog state on tenant change to prevent stale data
    setEditDialogOpen(false);
    setDeleteDialogOpen(false);
    setSelectedBenchmark(null);
    setScopeFilter("all");
    
    // Fetch fresh data for new tenant
    fetchData();
  }, [tenantId, fetchData]);

  // Build KPI display name map
  const kpiDisplayNames = useMemo(() => {
    const map = {};
    kpiMetadata?.supported_kpis?.forEach((kpi) => {
      map[kpi.kpi_name] = kpi.display_name;
    });
    return map;
  }, [kpiMetadata]);

  // Filter benchmarks
  const filteredBenchmarks = useMemo(() => {
    if (scopeFilter === "all") return benchmarks;
    return benchmarks.filter((b) => b.scope_type === scopeFilter);
  }, [benchmarks, scopeFilter]);

  // Open create dialog
  const handleCreate = () => {
    setSelectedBenchmark(null);
    setDialogMode("create");
    setEditDialogOpen(true);
  };

  // Open edit dialog
  const handleEdit = (benchmark) => {
    setSelectedBenchmark(benchmark);
    setDialogMode("edit");
    setEditDialogOpen(true);
  };

  // Open delete dialog
  const handleDelete = (benchmark) => {
    setSelectedBenchmark(benchmark);
    setDeleteDialogOpen(true);
  };

  // Format target value based on KPI type
  const formatTarget = (benchmark) => {
    const kpi = benchmark.kpi_name || benchmark.metric;
    const value = benchmark.target_value;

    if (kpi?.includes("rate") || kpi?.includes("conversion")) {
      return pct(value);
    }
    if (kpi?.includes("revenue") || kpi?.includes("cpa") || kpi?.includes("profit") || kpi?.includes("cpl")) {
      return currency(value);
    }
    if (kpi?.includes("hours")) {
      return `${value}h`;
    }
    return value?.toString() || "-";
  };

  return (
    <Card className="glass" data-testid="benchmark-manager">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-slate-100 flex items-center gap-2">
              <Target className="h-5 w-5 text-indigo-400" />
              Benchmark Manager
            </CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              Set performance targets and variance thresholds for KPIs
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {/* Scope Filter */}
            <Select value={scopeFilter} onValueChange={setScopeFilter}>
              <SelectTrigger className="w-[130px] h-8 bg-white/5 text-slate-100 border-white/10 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Scopes</SelectItem>
                <SelectItem value="global">Global</SelectItem>
                <SelectItem value="product">Product</SelectItem>
                <SelectItem value="campaign">Campaign</SelectItem>
                <SelectItem value="agent">Agent</SelectItem>
                <SelectItem value="script">Script</SelectItem>
              </SelectContent>
            </Select>

            {/* Add Button */}
            <Button
              onClick={handleCreate}
              data-testid="add-benchmark-btn"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Benchmark
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden rounded-2xl ring-1 ring-white/10">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-slate-200/70">KPI</TableHead>
                <TableHead className="text-slate-200/70">Scope</TableHead>
                <TableHead className="text-slate-200/70 text-right">Target</TableHead>
                <TableHead className="text-slate-200/70 text-right">Warning</TableHead>
                <TableHead className="text-slate-200/70 text-right">Critical</TableHead>
                <TableHead className="text-slate-200/70">Last Updated</TableHead>
                <TableHead className="text-slate-200/70 w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="border-white/10">
                  <TableCell colSpan={7} className="py-10 text-center text-sm text-slate-200/70">
                    Loading benchmarks…
                  </TableCell>
                </TableRow>
              ) : filteredBenchmarks.length === 0 ? (
                <TableRow className="border-white/10">
                  <TableCell colSpan={7} className="py-10 text-center text-sm text-slate-200/70">
                    {scopeFilter === "all"
                      ? "No benchmarks configured yet."
                      : `No ${SCOPE_LABELS[scopeFilter]} benchmarks found.`}
                  </TableCell>
                </TableRow>
              ) : (
                filteredBenchmarks.map((benchmark) => {
                  const kpi = benchmark.kpi_name || benchmark.metric;
                  return (
                    <TableRow
                      key={benchmark.id}
                      className="border-white/10"
                      data-testid={`benchmark-row-${benchmark.id}`}
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-slate-100 font-medium">
                            {kpiDisplayNames[kpi] || kpi}
                          </div>
                          <div className="text-xs text-slate-400">{kpi}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`text-xs border ${
                            SCOPE_COLORS[benchmark.scope_type] || SCOPE_COLORS.global
                          }`}
                        >
                          {SCOPE_LABELS[benchmark.scope_type] || "Global"}
                        </Badge>
                        {benchmark.scope_id && (
                          <div className="text-xs text-slate-400 mt-1 truncate max-w-[120px]">
                            {benchmark.scope_id}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-100">
                        {formatTarget(benchmark)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-amber-400">
                        {pct(benchmark.variance_threshold_warning || benchmark.variance_threshold || 0.1)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-400">
                        {pct(benchmark.variance_threshold_critical || 0.2)}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-slate-400">
                          {benchmark.updated_at
                            ? new Date(benchmark.updated_at).toLocaleDateString()
                            : "-"}
                        </div>
                        {benchmark.updated_by_email && (
                          <div className="text-xs text-slate-500 truncate max-w-[100px]">
                            by {benchmark.updated_by_email}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-400 hover:text-slate-100 hover:bg-white/10"
                            onClick={() => handleEdit(benchmark)}
                            data-testid={`edit-benchmark-${benchmark.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                            onClick={() => handleDelete(benchmark)}
                            data-testid={`delete-benchmark-${benchmark.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Summary */}
        <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
          <span>
            {filteredBenchmarks.length} benchmark{filteredBenchmarks.length !== 1 ? "s" : ""}{" "}
            {scopeFilter !== "all" && `(filtered by ${SCOPE_LABELS[scopeFilter]})`}
          </span>
          <span>
            Scope resolution: script → agent → campaign → product → global
          </span>
        </div>
      </CardContent>

      {/* Edit/Create Dialog */}
      <BenchmarkDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        benchmark={selectedBenchmark}
        kpiMetadata={kpiMetadata}
        onSave={fetchData}
        mode={dialogMode}
      />

      {/* Delete Dialog */}
      <DeleteBenchmarkDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        benchmark={selectedBenchmark}
        onDelete={fetchData}
      />
    </Card>
  );
}
